import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import type { SettingsPane } from '../analytics/analytics.events';
import { AnalyticsService } from '../analytics/analytics.service';
import * as fromAppI18n from '../app.i18n';
import { ConnectorsPanelComponent } from '../connectors/connectors-panel.component';
import { LayoutActions } from '../layout/store/layout.actions';
import { layoutFeature } from '../layout/store/layout.reducer';
import { SettingsMemoryComponent } from './settings-memory.component';
import {
  selectAvailableSettingsPanes,
  selectSettingsVisible,
  selectVisibleSettingsPane,
} from './settings.selectors';

const PANES: Record<
  SettingsPane,
  { label: string; icon: fromSeriousSystem.IconsType }
> = {
  preferences: {
    label: 'SETTINGS.NAV.PREFERENCES',
    icon: { type: 'uiBasic', icon: 'filters' },
  },
  memory: {
    label: 'SETTINGS.NAV.MEMORY',
    icon: { type: 'uiBasic', icon: 'book-alt' },
  },
  connectors: {
    label: 'SETTINGS.NAV.CONNECTORS',
    icon: { type: 'teammateIcon', icon: 'apps-add' },
  },
};

/**
 * The user's settings window (SB-6520, Figma 12817-27213): Preferences, Memory and Connectors, one
 * `sd-modal` with its chrome stood down and `expandedSurface` on. Desktop shows the panes in a left
 * sidebar beside the selected one; a phone shows the pane list first, then the pane with a back
 * arrow (KD-9). Only the panes the person has access to are listed (Rule 1).
 *
 * Open state, pane and source live in the layout store. The body only exists while the window
 * shows, so every open re-reads memory and starts the connectors list on "All".
 */
@Component({
  selector: 'squadbox-settings-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    ConnectorsPanelComponent,
    SettingsMemoryComponent,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.ModalComponent,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseIconDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
  ],
  template: `
    <sd-modal
      [isModalVisible]="visible()"
      [modalOptions]="{
        title: '',
        ariaLabel: translateService.instant('SETTINGS.TITLE'),
        showCloseButton: false,
        backdropDismiss: true,
        actions: [],
        maxWidth: 1016,
        expandedSurface: true,
        bodyClass: '[--modal-content-padding:0px]',
        footerClass: 'hidden',
      }"
      (triggerAction)="close()"
    >
      @if (visible()) {
        <!-- 800px tall on desktop whatever the pane holds, never past the modal's own cap. -->
        <div
          class="flex flex-1 min-h-0 text-neutral-900 tablet-landscape:flex-none tablet-landscape:h-[min(50rem,calc(100svh-10rem))]"
        >
          @if (!isMobile()) {
            <nav
              class="flex w-54 shrink-0 flex-col gap-1 px-4 py-8 border-r border-neutral-200 bg-gradient-to-b from-neutral-50 to-neutral-100"
              [attr.aria-label]="'SETTINGS.TITLE' | translate"
            >
              <span
                class="px-3 py-2 typo-caption leading-4 text-neutral-500 truncate"
                >{{ 'SETTINGS.TITLE' | translate }}</span
              >
              @for (item of panes(); track item.pane) {
                <button
                  type="button"
                  class="flex w-full items-center gap-2 px-2 py-1 rounded-lg typo-p3 leading-6 text-neutral-700 text-left"
                  [class]="
                    item.pane === pane()
                      ? 'bg-neutral-200 font-semibold'
                      : 'hover:bg-neutral-100'
                  "
                  [attr.aria-current]="item.pane === pane() ? 'page' : null"
                  (click)="select(item.pane)"
                >
                  <svg [sdUseIcon]="item.icon" class="size-5 shrink-0"></svg>
                  {{ item.label | translate }}
                </button>
              }
            </nav>
          }

          @if (pane() === 'connectors') {
            <squadbox-connectors-panel
              [showBack]="isMobile()"
              (back)="showList()"
            />
          } @else {
            <div class="flex flex-1 min-w-0 flex-col">
              <div class="relative shrink-0 flex items-center px-4 pt-8">
                @if (isMobile() && pane()) {
                  <button
                    sdIconButton
                    variant="icon"
                    color="neutral"
                    size="xs"
                    [attr.aria-label]="'SETTINGS.BACK' | translate"
                    (click)="showList()"
                  >
                    <svg sdUseArrowIcon="arrow-left"></svg>
                  </button>
                }
                <h2
                  class="m-0 flex-1 min-w-0 px-4 typo-h6 leading-8 font-semibold truncate"
                >
                  {{ title() | translate }}
                </h2>
                <button
                  sdIconButton
                  variant="icon"
                  color="neutral"
                  size="xs"
                  class="absolute top-4 right-4"
                  [attr.aria-label]="'SETTINGS.CLOSE' | translate"
                  (click)="close()"
                >
                  <svg sdUseUIBasicIcon="close"></svg>
                </button>
              </div>

              <div
                class="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto px-5 pt-6 pb-10 tablet-landscape:px-8"
              >
                @switch (pane()) {
                  @case (null) {
                    <!-- The phone's first screen: one row per pane, in the drawer's row style. -->
                    <ul class="m-0 flex list-none flex-col gap-1 p-0">
                      @for (item of panes(); track item.pane) {
                        <li>
                          <button
                            type="button"
                            class="flex w-full items-center gap-3 rounded-lg px-3 py-3 typo-p2 text-neutral-900 text-left hover:bg-neutral-100"
                            (click)="select(item.pane)"
                          >
                            <svg
                              [sdUseIcon]="item.icon"
                              class="size-5 shrink-0 text-neutral-700"
                            ></svg>
                            <span class="flex-1 min-w-0 truncate">{{
                              item.label | translate
                            }}</span>
                            <svg
                              sdUseArrowIcon="chevron-right"
                              class="size-5 shrink-0 text-neutral-500"
                            ></svg>
                          </button>
                        </li>
                      }
                    </ul>
                  }
                  @case ('memory') {
                    <hr class="my-2 border-neutral-200" />
                    <squadbox-settings-memory />
                  }
                  @default {
                    <hr class="my-2 border-neutral-200" />
                    <!-- Side by side on desktop; on a phone the selector goes below, full width. -->
                    <div
                      class="flex flex-col gap-3 px-3 tablet-landscape:flex-row tablet-landscape:items-center tablet-landscape:gap-1"
                    >
                      <div class="flex flex-1 min-w-0 flex-col gap-1">
                        <label
                          for="settings-language"
                          class="typo-p3 leading-5 font-semibold text-neutral-900"
                          >{{
                            'SETTINGS.PREFERENCES.LANGUAGE' | translate
                          }}</label
                        >
                        <p class="m-0 typo-p3 leading-5 text-neutral-500">
                          {{ 'SETTINGS.PREFERENCES.LANGUAGE_HELP' | translate }}
                        </p>
                      </div>
                      <div class="relative shrink-0">
                        <!-- A native select: the platform's own keyboard and screen-reader behavior,
                             and no change event for the language already chosen (spec 2.3). -->
                        <select
                          id="settings-language"
                          class="h-10 w-full cursor-pointer appearance-none rounded-lg bg-shades-white pl-3 pr-8 typo-p3 leading-5 tablet-landscape:w-auto"
                          [value]="language"
                          (change)="setLanguage($any($event.target).value)"
                        >
                          <option value="en">English</option>
                          <option value="fr">Français</option>
                        </select>
                        <svg
                          sdUseArrowIcon="chevron-down"
                          class="pointer-events-none absolute right-2 top-1/2 size-5 -translate-y-1/2 text-neutral-700"
                        ></svg>
                      </div>
                    </div>
                    <hr class="my-2 border-neutral-200" />
                  }
                }
              </div>
            </div>
          }
        </div>
      }
    </sd-modal>
  `,
})
export class SettingsModalComponent {
  private readonly store = inject(Store);
  private readonly analyticsService = inject(AnalyticsService);
  public readonly translateService = inject(TranslateService);

  protected readonly visible = this.store.selectSignal(selectSettingsVisible);
  /** Null on a phone's pane list. */
  protected readonly pane = this.store.selectSignal(selectVisibleSettingsPane);
  protected readonly isMobile = this.store.selectSignal(
    layoutFeature.selectIsMobile,
  );
  private readonly settings = this.store.selectSignal(
    layoutFeature.selectSettings,
  );
  private readonly available = this.store.selectSignal(
    selectAvailableSettingsPanes,
  );

  protected readonly panes = computed(() =>
    this.available().map((pane) => ({ pane, ...PANES[pane] })),
  );
  protected readonly title = computed(() => {
    const pane = this.pane();
    return pane ? PANES[pane].label : 'SETTINGS.TITLE';
  });
  protected readonly language = this.translateService.getCurrentLang();

  /** The pane last reported, and the open it belonged to; reset when the window goes away. */
  private lastView: { pane: SettingsPane; openCount: number } | null = null;

  constructor() {
    // settings_section_viewed (KD-7): once per pane shown. The first view after an open reports
    // where it was opened from, later ones `settings-navigation`. Nothing for the phone's pane
    // list, for a request aimed at the pane already showing, or for a fallback to Preferences.
    effect(() => {
      const pane = this.pane();
      const settings = this.settings();
      if (!this.visible()) {
        this.lastView = null;
        return;
      }
      if (pane === null || pane === this.lastView?.pane) {
        return;
      }
      const firstAfterOpen = this.lastView?.openCount !== settings.openCount;
      this.lastView = { pane, openCount: settings.openCount };
      if (settings.fellBack) {
        return;
      }
      this.analyticsService
        .sendSettingsSectionViewedEvent({
          section: pane,
          source:
            firstAfterOpen && settings.openedFrom
              ? settings.openedFrom
              : 'settings-navigation',
          isDefaultSection:
            pane === 'preferences' && settings.selectedPane === null,
        })
        .subscribe();
    });
  }

  protected select(pane: SettingsPane): void {
    this.store.dispatch(LayoutActions.selectSettingsPane({ pane }));
  }

  protected showList(): void {
    this.store.dispatch(LayoutActions.showSettingsPaneList());
  }

  protected close(): void {
    this.store.dispatch(LayoutActions.closeSettings());
  }

  protected setLanguage(language: string): void {
    fromAppI18n.setLanguageInLocalStorageAndReload(
      language === 'fr' ? 'fr' : 'en',
    );
  }
}
